import React, { forwardRef, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import AppMessageBoxWrapper from "../UtilsComponents/AppMessageBoxWrapper";
import { StyledMessageText } from "./../../Styles/StyledComponents";
import { processHTMLLinks } from "../../utils/processtext";
import { Box } from "@mui/material";
import TextToSpeechButton from "../Features/AppRenderTextToSpeech";
import { useAppSelector } from "../../../Store";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { sanitizeHtml } from "../../utils/security/sanitizeHtml";

interface Props {
  isFullScreen?: boolean;
  userMessage: boolean;
  children: any;
  time: string;
  readStatus?: "sent" | "delivered" | "seen";
  isError?: boolean;
  ref?: any;
  [otherProps: string]: any;
}
const AppRenderText: React.FC<Props> = React.memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        userMessage = false,
        children,
        time,
        readStatus = "delivered",
        isFullScreen,
        isError = false,
        ...otherProps
      },
      ref
    ) => {
      const [isSpeaking, setIsSpeaking] = React.useState(false);
      const { botConfig } = useAppSelector(useBotConfigStore);

      const isTextToSpeechEnabled = useMemo(() => {
        return botConfig?.config?.voice_settings?.text_to_speech_enabled ?? false;
      }, [botConfig?.config?.voice_settings?.text_to_speech_enabled]);

      const speakText = (htmlText: any) => {
        if ("speechSynthesis" in window) {
          // Use a DOMParser to parse the HTML and extract text content
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText || "", "text/html");
          const text = doc.body.textContent || "";

          if (text.trim() === "") {
            // No text to speak, return early
            return;
          }

          const utterance = new SpeechSynthesisUtterance(text);

          // Set additional options for the utterance if needed
          // utterance.voice = ...
          // utterance.rate = ...

          // Event handler for when speech ends
          utterance.onend = () => {
            setIsSpeaking(false);
          };

          // Start or stop speaking based on the current state
          if (isSpeaking) {
            window.speechSynthesis.cancel();
          } else {
            window.speechSynthesis.speak(utterance);
          }
          setIsSpeaking(!isSpeaking);
        }
      };
      const processedText = useMemo(() => sanitizeHtml(processHTMLLinks(children) ?? ''), [children]);

      // Strip HTML tags for clean text rendering
      const stripHtmlTags = (html: string): string => {
        if (typeof html !== 'string') return html;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.body.textContent || '';
      };

      const cleanText = useMemo(() => stripHtmlTags(children), [children]);

      if (!children || children?.length < 1) {
        return null;
      }

      return (
        <AppMessageBoxWrapper
          userMessage={userMessage}
          time={time}
          isMedia={false}
          readStatus={readStatus}
          ref={ref}
          isError={isError}
        >
          {/* Text To Speech */}
          {userMessage ? null : (
            isTextToSpeechEnabled && (
              <Box
                className="speaker"
                sx={{ position: "absolute", right: "-8px", top: "-6px" }}
            >
                <TextToSpeechButton text={children} />
              </Box>
            )
          )}

          {!userMessage ? (
            <StyledMessageText
              isFullScreen={isFullScreen}
              userMessage={userMessage}
              isError={isError}
              {...otherProps}
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => <span>{children}</span>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  img: ({ src, alt }) => (
                    <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />
                  ),
                  ul: ({ children }) => <ul>{children}</ul>,
                  ol: ({ children }) => <ol>{children}</ol>,
                  li: ({ children }) => <li>{children}</li>,
                  h1: ({ children }) => <h1>{children}</h1>,
                  h2: ({ children }) => <h2>{children}</h2>,
                  h3: ({ children }) => <h3>{children}</h3>,
                  h4: ({ children }) => <h4>{children}</h4>,
                  h5: ({ children }) => <h5>{children}</h5>,
                  h6: ({ children }) => <h6>{children}</h6>,
                  code: ({ inline, children }:any) => 
                    inline ? <code>{children}</code> : <pre><code>{children}</code></pre>,
                  blockquote: ({ children }) => <blockquote>{children}</blockquote>,
                  br: () => <br />,
                  hr: () => <hr />,
                }}
              >
                {cleanText}
              </ReactMarkdown>
            </StyledMessageText>
          ) : (
            <StyledMessageText
              isFullScreen={isFullScreen}
              userMessage={userMessage}
              isError={isError}
              {...otherProps}
              dangerouslySetInnerHTML={{ __html: processedText }}
            />
          )}
        </AppMessageBoxWrapper>
      );
    }
  )
);

export default AppRenderText;
